import { motion, useMotionValue, useTransform, animate } from 'motion/react';
import { useState, useEffect, useLayoutEffect, useRef, useMemo, ReactNode } from 'react';
import chroma from 'chroma-js';

let defaultInteriorColor = '#c53a68';
let defaultSealBackground = '#e15370';
let defaultSealForeground = '#6b2740';

function deriveColors(baseColor: string) {
  let base = chroma(baseColor);
  return {
    interior: base.hex(),
    sealBackground: base.brighten(0.5).saturate(0.3).hex(),
    sealForeground: base.darken(1.5).hex(),
  };
}

export let EnvelopeIcon = ({
  width = 64,
  className,
  isOpen: isOpenProp = false,
  seal = false,
  sealInitial,
  onIsOpenChange,
  onOpenAnimationComplete,
  paperContent,
  closeDelay = 0,
  color,
}: {
  width?: number;
  className?: string;
  isOpen?: boolean;
  seal?: boolean;
  sealInitial?: string;
  onIsOpenChange?: (isOpen: boolean) => void;
  onOpenAnimationComplete?: () => void;
  paperContent?: ReactNode;
  closeDelay?: number;
  color?: string;
}) => {
  let height = width * 0.68;
  let [isOpenInternal, setIsOpenInternal] = useState(isOpenProp);
  let sealTextRef = useRef<SVGTextElement>(null);

  let colors = useMemo(() => {
    if (color) {
      return deriveColors(color);
    }
    return {
      interior: defaultInteriorColor,
      sealBackground: defaultSealBackground,
      sealForeground: defaultSealForeground,
    };
  }, [color]);
  let prevIsOpenRef = useRef(isOpenProp);

  let isClosing = prevIsOpenRef.current && !isOpenInternal;
  let animationDelay = isClosing ? closeDelay : 0;

  useEffect(() => {
    prevIsOpenRef.current = isOpenInternal;
  }, [isOpenInternal]);

  useEffect(() => {
    setIsOpenInternal(isOpenProp);
  }, [isOpenProp]);

  useLayoutEffect(() => {
    if (sealTextRef.current && sealInitial) {
      let canvas = document.createElement('canvas');
      let ctx = canvas.getContext('2d');
      if (!ctx) return;

      ctx.font = "500 72px 'DM Mono', monospace";
      let metrics = ctx.measureText(sealInitial);

      let offsetX = 50 - (metrics.actualBoundingBoxRight - metrics.actualBoundingBoxLeft) / 2;
      let offsetY = 50 + (metrics.actualBoundingBoxAscent - metrics.actualBoundingBoxDescent) / 2;

      sealTextRef.current.setAttribute('x', String(offsetX));
      sealTextRef.current.setAttribute('y', String(offsetY));
    }
  }, [sealInitial]);

  let handleClick = () => {
    if (!isOpenInternal) {
      setIsOpenInternal(true);
      onIsOpenChange?.(true);
    }
  };

  let gentleSpring = { type: 'spring' as const, stiffness: 120, damping: 14 };
  let gentleClampTween = {
    type: 'tween' as const,
    ease: [0.25, 0.1, 0.25, 1] as [number, number, number, number],
    duration: 0.4,
  };

  let rotateX = useMotionValue(isOpenInternal ? 180 : 0);

  useEffect(() => {
    let controls = animate(rotateX, isOpenInternal ? 180 : 0, {
      ...gentleSpring,
      delay: animationDelay / 1000,
      onComplete: () => {
        if (isOpenInternal) onOpenAnimationComplete?.();
      },
    });
    return () => controls.stop();
  }, [isOpenInternal, animationDelay, onOpenAnimationComplete]);

  let flapZIndex = useTransform(rotateX, (r) => (r >= 90 ? 2 : 5));
  let flapColor = useTransform(rotateX, (r) =>
    r >= 90 ? colors.interior : '#d4d4d4',
  );

  let paperWidth = width * 0.85;
  let paperHeight = height * 1.6;

  return (
    <div
      className={className}
      style={{
        position: 'relative',
        width,
        height,
        isolation: 'isolate',
      }}
      onClick={handleClick}
    >
      {/* Interior box */}
      <div
        style={{
          position: 'absolute',
          backgroundColor: colors.interior,
          borderRadius: `0 0 ${width * 0.06}px ${width * 0.06}px`,
          boxShadow: '0px 2px 8px -2px rgba(0,0,0,0.2)',
          zIndex: 1,
          top: 0,
          left: 2,
          height: height - 2,
          width: width - 4,
        }}
      />

      {/* Paper container */}
      <div
        style={{
          position: 'absolute',
          left: '50%',
          marginLeft: -paperWidth / 2,
          top: -paperHeight,
          width: paperWidth,
          height: paperHeight + height - 2,
          overflow: 'hidden',
          zIndex: 3,
          pointerEvents: 'none',
        }}
      >
        <motion.div
          style={{
            position: 'absolute',
            left: 0,
            right: 0,
            height: paperHeight,
            bottom: 0,
          }}
          animate={{ y: isOpenInternal ? paperHeight * 0.1 : paperHeight }}
          transition={{ ...gentleSpring, delay: animationDelay / 1000 }}
        >
          <div
            style={{
              height: '100%',
              width: '100%',
              backgroundColor: 'white',
              borderRadius: width * 0.02,
              boxShadow: '0px 2px 6px rgba(0,0,0,0.1)',
              padding: width * 0.08,
              boxSizing: 'border-box',
            }}
          >
            {paperContent}
          </div>
        </motion.div>
      </div>

      {/* Flap (animated) */}
      <motion.div
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          width,
          height: height / 2,
          transformStyle: 'preserve-3d',
          transformOrigin: 'top center',
          zIndex: flapZIndex,
          rotateX,
        }}
      >
        <motion.div
          style={{
            width: 0,
            height: 0,
            borderLeftWidth: width / 2,
            borderRightWidth: width / 2,
            borderTopWidth: height / 2 + height / 18,
            borderBottomWidth: height / 2 - height / 18,
            borderColor: 'transparent',
            borderTopColor: flapColor,
          }}
        />
      </motion.div>

      {/* Front face triangles */}
      <div
        style={{
          position: 'absolute',
          zIndex: 4,
          top: 0,
          left: 0,
          height,
          width,
        }}
      >
        <div
          style={{
            width: 0,
            height: 0,
            borderBottomWidth: height / 2,
            borderTopWidth: height / 2,
            borderLeftWidth: width / 2,
            borderRightWidth: width / 2,
            borderColor: 'transparent',
            borderLeftColor: '#f5f5f5',
            borderRightColor: '#f5f5f5',
            borderBottomColor: '#e5e5e5',
            borderRadius: `0 0 ${width * 0.06}px ${width * 0.06}px`,
          }}
        />
      </div>

      {/* Seal */}
      {seal && (
        <motion.div
          style={{
            position: 'absolute',
            zIndex: 6,
            top: '50%',
            left: '50%',
            width: width * 0.26,
            height: width * 0.26,
            marginTop: -width * 0.13,
            marginLeft: -width * 0.13,
          }}
          animate={{
            scale: isOpenInternal ? 0 : 1,
            opacity: isOpenInternal ? 0 : 1,
          }}
          transition={{ ...gentleClampTween, delay: animationDelay / 1000 }}
        >
          <svg width="100%" height="100%" viewBox="0 0 100 100" style={{ overflow: 'visible' }}>
            {/* Gloopy wax blob */}
            <g transform="translate(50, 50) scale(0.082) translate(-2098, -2189)">
              <path
                fill={colors.sealBackground}
                d="M2730.4,2037.5c-35.8-139.9-106.9-276-233.4-362.3c-366.8-250.4-1002.2,11.5-1052.2,435.5
                  c-16.7,141.5,18.5,289,91.3,413.4c37.1,63.4,86.4,118,150.9,158.1c69.5,43.3,150.3,73.2,230.7,93.3c155,38.7,323,37,472.3-21.3
                  c136.4-53.3,253.3-150.8,313.5-278.8C2767.7,2338.4,2767.1,2181.2,2730.4,2037.5z"
              />
            </g>
            {/* Perfect circle border */}
            <circle
              cx="50"
              cy="50"
              r="43"
              fill={colors.sealBackground}
              stroke={colors.sealForeground}
              strokeWidth="7"
            />
            <text
              ref={sealTextRef}
              fill={colors.sealForeground}
              fontSize={72}
              fontFamily="'DM Mono', monospace"
              fontWeight={500}
            >
              {sealInitial}
            </text>
          </svg>
        </motion.div>
      )}
    </div>
  );
};
